import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { ElementItemNode, ElementItemStoreType } from './types/types'

/**
 * Creates a zustand store for managing workspace elementItemValue state
 * @returns {ElementItemStoreType} A reactive store with workspace management methods
 */
export const useElementItemStore = create<ElementItemStoreType>()((set) => ({
    elementItemValue: {
        id: '',
        actionFlag: '',
        elementId: '',
        elementType: '',
        isProductTypeProject: false,
        isProjectProductFamily: false,
    },
    selectedElementFormat: undefined,
    selectedElementFilterValue: undefined,
    setElementItemValue: (elem: ElementItemNode) =>
        set({
            elementItemValue: { ...elem },
        }),
    setSelectedElementFormat: (format: string) =>
        set({
            selectedElementFormat: format,
        }),
    setSelectedElementFilterValue: (filterValue: string) =>
        set({
            selectedElementFilterValue: filterValue,
        }),
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `useElementItemStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { elementItemValue, setElementItemValue } = useElementItemShallowSelector((state) => ({
 *   elementItemValue: state.elementItemValue,
 *   setElementItemValue: state.setElementItemValue,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useElementItemShallowSelector = <T>(
    selector: (state: ElementItemStoreType) => T
): T => useElementItemStore(useShallow(selector))
